import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setHeatmapLabelDigits,
  setHeatmapLabelsAdaptive,
  setHeatmapLabelsEnabled,
  setHeatmapLabelsSizePercent,
} from "../redux/sceneGlobal";
import {
  useActiveHeatmapLabelLayer,
  useHasHeatmapLabelLayer,
} from "./CustomHooks";
import { Switch } from "./Switch";
import {
  HEATMAP_LABEL_LAYER_LABELS,
  MAX_HEATMAP_LABEL_DIGITS,
  MIN_HEATMAP_LABEL_DIGITS,
} from "./threeDComponent/heatmapLabelLayers";
import "./HeatmapLabelsToggle.css";

export const LABEL_SIZE_PERCENTS = [
  10, 25, 50, 75, 90, 100, 125, 150, 200, 250, 300, 400, 500,
];

const HeatmapLabelsToggle = () => {
  const dispatch = useDispatch();
  const heatmapLabelsEnabled = useSelector(
    (state) => state.sceneVar.heatmapLabelsEnabled,
  );
  const adaptive = useSelector((state) => state.sceneVar.heatmapLabelsAdaptive);
  const sizePercent = useSelector(
    (state) => state.sceneVar.heatmapLabelsSizePercent,
  );
  const digitsByLayer = useSelector(
    (state) => state.sceneVar.heatmapLabelDigitsByLayer,
  );
  const hasLabelLayer = useHasHeatmapLabelLayer();
  const activeLayer = useActiveHeatmapLabelLayer();
  const activeDigits = activeLayer ? (digitsByLayer?.[activeLayer] ?? 1) : 1;
  const activeLayerLabel = activeLayer
    ? HEATMAP_LABEL_LAYER_LABELS[activeLayer]
    : "";

  if (!hasLabelLayer) {
    return null;
  }

  let resolvedIndex = LABEL_SIZE_PERCENTS.indexOf(sizePercent);
  if (resolvedIndex === -1) {
    resolvedIndex = LABEL_SIZE_PERCENTS.findIndex((pct) => pct >= sizePercent);
    if (resolvedIndex === -1) resolvedIndex = LABEL_SIZE_PERCENTS.length - 1;
  }
  const currentPercent = LABEL_SIZE_PERCENTS[resolvedIndex];
  const canDecrease = resolvedIndex > 0;
  const canIncrease = resolvedIndex < LABEL_SIZE_PERCENTS.length - 1;
  const canDecreaseDigits = activeDigits > MIN_HEATMAP_LABEL_DIGITS;
  const canIncreaseDigits = activeDigits < MAX_HEATMAP_LABEL_DIGITS;

  return (
    <div className="heatmap-labels-controls">
      <Switch
        checked={heatmapLabelsEnabled}
        onChange={(checked) => dispatch(setHeatmapLabelsEnabled(checked))}
        label="Labels"
        title={heatmapLabelsEnabled ? "Hide value labels" : "Show value labels"}
      />

      {heatmapLabelsEnabled && (
        <div
          className="heatmap-labels-settings"
          title="Label text size settings"
        >
          <Switch
            checked={adaptive}
            onChange={(checked) => dispatch(setHeatmapLabelsAdaptive(checked))}
            label="Adaptive Size"
            title={
              adaptive
                ? "Adaptive: size follows each zone, scaled by %"
                : "Fixed: same base size for all labels, scaled by %"
            }
          />

          <div className="heatmap-labels-size">
            <button
              type="button"
              className="heatmap-labels-size__btn"
              disabled={!canDecrease}
              aria-label="Decrease label size"
              title="Decrease label size"
              onClick={() =>
                dispatch(
                  setHeatmapLabelsSizePercent(
                    LABEL_SIZE_PERCENTS[resolvedIndex - 1],
                  ),
                )
              }
            >
              −
            </button>
            <span className="heatmap-labels-size__value" title="Label size">
              {currentPercent}%
            </span>
            <button
              type="button"
              className="heatmap-labels-size__btn"
              disabled={!canIncrease}
              aria-label="Increase label size"
              title="Increase label size"
              onClick={() =>
                dispatch(
                  setHeatmapLabelsSizePercent(
                    LABEL_SIZE_PERCENTS[resolvedIndex + 1],
                  ),
                )
              }
            >
              +
            </button>
          </div>

          {activeLayer && (
            <div
              className="heatmap-labels-size heatmap-labels-digits"
              title={`${activeLayerLabel} decimal digits`}
            >
              <span className="heatmap-labels-digits__label">Digits</span>
              <button
                type="button"
                className="heatmap-labels-size__btn"
                disabled={!canDecreaseDigits}
                aria-label={`Decrease ${activeLayerLabel} decimal digits`}
                title={`Decrease ${activeLayerLabel} digits`}
                onClick={() =>
                  dispatch(
                    setHeatmapLabelDigits({
                      layer: activeLayer,
                      digits: activeDigits - 1,
                    }),
                  )
                }
              >
                −
              </button>
              <span
                className="heatmap-labels-size__value"
                title={`${activeLayerLabel} digits`}
              >
                {activeDigits}
              </span>
              <button
                type="button"
                className="heatmap-labels-size__btn"
                disabled={!canIncreaseDigits}
                aria-label={`Increase ${activeLayerLabel} decimal digits`}
                title={`Increase ${activeLayerLabel} digits`}
                onClick={() =>
                  dispatch(
                    setHeatmapLabelDigits({
                      layer: activeLayer,
                      digits: activeDigits + 1,
                    }),
                  )
                }
              >
                +
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeatmapLabelsToggle;
